import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Panel } from "@/ui/Panel";
import { Button, Spinner } from "@/ui/Button";
import { ErrorState } from "@/ui/States";
export const Route = createFileRoute("/_app/connections/github")({
  component: GitHubInstallCallback,
  validateSearch: (
    search: Record<string, unknown>,
  ): { installation_id?: string; state?: string; code?: string; setup_action?: string } => ({
    ...(text(search.installation_id) !== undefined
      ? { installation_id: text(search.installation_id) }
      : {}),
    ...(text(search.state) !== undefined ? { state: text(search.state) } : {}),
    ...(text(search.code) !== undefined ? { code: text(search.code) } : {}),
    ...(text(search.setup_action) !== undefined
      ? { setup_action: text(search.setup_action) }
      : {}),
  }),
});

/**
 * A search parameter as text, whatever the router made of it.
 *
 * The router parses query values as JSON, so an installation id — which is
 * always digits — arrives as a number. Reading it with `typeof === "string"`
 * silently dropped it, and it reached the API as a JSON number, where the
 * schema wanted a string: "validation failed", with nothing on the page saying
 * which field or why.
 */
function text(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return undefined;
}

/**
 * Where GitHub sends somebody back after they have chosen repositories.
 *
 * This page holds no authority of its own. It carries two values out of the
 * query string to the API, which decides whether they mean anything: the state
 * is single-use, expires, and only counts for the person who started the flow.
 * A link to this page, replayed or forwarded, connects nothing.
 */
function GitHubInstallCallback() {
  const { installation_id: installationId, state, code, setup_action: action } = Route.useSearch();
  const navigate = useNavigate();

  const complete = useMutation({
    mutationFn: () =>
      api.post<{ organisation_id: string }>("/sources/github/install/complete", {
        state,
        installation_id: installationId,
        // GitHub issues this to the person who just installed the App. It is
        // what proves the installation is theirs; without it the id in the URL
        // is a number anybody could have typed.
        code,
      }),
    onSuccess: (result) => {
      void navigate({
        to: "/o/$orgId/connections",
        params: { orgId: result.organisation_id },
        replace: true,
      });
    },
  });

  useEffect(() => {
    if (installationId && state && code) complete.mutate();
    // Redeemed once, on arrival, for what the URL carried.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installationId, state, code]);

  // GitHub sends people here after a permissions change too, with no
  // installation id. That is not an error and must not be reported as one.
  if (!installationId || !state || !code) {
    return (
      <Panel>
        <h1 className="text-sm font-medium text-[var(--text-primary)]">
          {action === "update" ? "Repository access updated" : "Nothing to connect"}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--text-secondary)]">
          {action === "update"
            ? "GitHub has recorded the change. Your existing connection still applies."
            : "This page finishes connecting GitHub. Start from an organisation's connections page."}
        </p>
        <div className="mt-4">
          <Button tone="secondary" size="dense" onClick={() => void navigate({ to: "/" })}>
            Back to your organisations
          </Button>
        </div>
      </Panel>
    );
  }

  if (complete.isError) {
    // A validation failure names a field, and saying which one is the
    // difference between "start again" and knowing why starting again will not
    // help. It was not shown, and the same message appeared for a request the
    // server had never even looked at.
    const fields =
      complete.error instanceof ApiError ? Object.entries(complete.error.fieldErrors()) : [];
    return (
      <div className="flex flex-col gap-4">
        <ErrorState error={complete.error} />
        {fields.length > 0 && (
          <ul className="text-[13px] text-[var(--text-secondary)]">
            {fields.map(([field, message]) => (
              <li key={field}>
                <code className="mono">{field}</code>: {message}
              </li>
            ))}
          </ul>
        )}
        <p className="text-[13px] text-[var(--text-secondary)]">
          Connecting has to be started from Drumlin, so that the installation lands in the
          organisation you meant. Open the organisation and choose Connect again.
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-40 items-center justify-center gap-3">
      <span className="relative h-5 w-5 text-[var(--primary)]">
        <Spinner />
      </span>
      <p className="text-[13px] text-[var(--text-secondary)]">Finishing the GitHub connection…</p>
    </div>
  );
}
